export type AutotuneRuntimePhase = 'discover' | 'local_search' | 'track';

export type AutotuneRunStartEvent = {
  type: 'run_start';
  runId: number | null;
  targetId: string;
  range: string | null;
  minStepHz: number | null;
  raw: string;
};

export type AutotunePhaseEvent = {
  type: 'phase';
  phase: AutotuneRuntimePhase;
  detail: string;
  raw: string;
};

export type AutotuneProbeEvent = {
  type: 'probe';
  phase: AutotuneRuntimePhase;
  role: string | null;
  iteration: number | null;
  frequencyHz: number;
  powerW: number;
  raw: string;
};

export type AutotuneRunResultEvent = {
  type: 'run_result';
  runId: number | null;
  reachedTrack: boolean;
  timeToTrackMs: number | null;
  trackFreqHz: number | null;
  trackPowerW: number | null;
  sessionPeakFreqHz: number | null;
  sessionPeakPowerW: number | null;
  raw: string;
};

export type AutotuneRuntimeEvent =
  | AutotuneRunStartEvent
  | AutotunePhaseEvent
  | AutotuneProbeEvent
  | AutotuneRunResultEvent;

type FreqPowerSample = {
  frequencyHz: number;
  powerMw: number;
};

export function parseAutotuneRuntimeLine(line: string): AutotuneRuntimeEvent[] {
  const trimmed = String(line ?? '').trim();
  if (!trimmed) {
    return [];
  }

  if (trimmed.startsWith('AUTOTUNE_RUN_START,')) {
    const payload = parseKeyValueCsv(trimmed);
    return [
      {
        type: 'run_start',
        runId: numberOrNull(payload.run),
        targetId: payload.target || 'unknown',
        range: payload.range || null,
        minStepHz: numberOrNull(payload.minStep),
        raw: trimmed,
      },
    ];
  }

  if (trimmed.startsWith('DISCOVER_SCAN,')) {
    const payload = parseKeyValueCsv(trimmed);
    const sample = parseFreqPowerToken(payload.sample);
    return sample
      ? [
          {
            type: 'probe',
            phase: 'discover',
            role: 'sample',
            iteration: numberOrNull(payload.iter),
            frequencyHz: sample.frequencyHz,
            powerW: sample.powerMw / 1000,
            raw: trimmed,
          },
        ]
      : [];
  }

  if (trimmed.startsWith('LOCAL_SEARCH_SET,')) {
    return parseProbeSetLine(trimmed, 'local_search', ['left', 'center', 'right']);
  }

  if (trimmed.startsWith('TRACK_SET,')) {
    return parseProbeSetLine(trimmed, 'track', ['left', 'center', 'right']);
  }

  if (trimmed.startsWith('AUTOTUNE_RUN_RESULT,')) {
    const payload = parseKeyValueCsv(trimmed);
    const trackPowerMw = numberOrNull(payload.trackPower);
    const peak = parseFreqPowerToken(payload.sessionPeak);
    return [
      {
        type: 'run_result',
        runId: numberOrNull(payload.run),
        reachedTrack: boolFrom01(payload.reachedTrack),
        timeToTrackMs: numberOrNull(payload.timeToTrackMs),
        trackFreqHz: numberOrNull(payload.trackFreq),
        trackPowerW: trackPowerMw == null ? null : trackPowerMw / 1000,
        sessionPeakFreqHz: peak?.frequencyHz ?? null,
        sessionPeakPowerW: peak == null ? null : peak.powerMw / 1000,
        raw: trimmed,
      },
    ];
  }

  const phaseMatch = trimmed.match(/^PHASE:\s+([A-Z_]+)\s+<-\s+(.+)$/);
  if (phaseMatch) {
    const phase = mapRuntimePhase(phaseMatch[1]);
    return phase
      ? [
          {
            type: 'phase',
            phase,
            detail: phaseMatch[2],
            raw: trimmed,
          },
        ]
      : [];
  }

  return [];
}

function parseProbeSetLine(
  line: string,
  phase: AutotuneRuntimePhase,
  sampleKeys: string[]
): AutotuneProbeEvent[] {
  const payload = parseKeyValueCsv(line);
  const iteration = numberOrNull(payload.iter);
  return sampleKeys.flatMap((key) => {
    const sample = parseFreqPowerToken(payload[key]);
    return sample
      ? [
          {
            type: 'probe' as const,
            phase,
            role: key,
            iteration,
            frequencyHz: sample.frequencyHz,
            powerW: sample.powerMw / 1000,
            raw: line,
          },
        ]
      : [];
  });
}

function parseFreqPowerToken(token: string | null | undefined): FreqPowerSample | null {
  if (!token || !token.includes('/')) {
    return null;
  }

  const [frequencyText, powerText] = token.split('/');
  const frequencyHz = Number(frequencyText);
  const powerMw = Number(powerText);
  if (!Number.isFinite(frequencyHz) || !Number.isFinite(powerMw)) {
    return null;
  }
  return { frequencyHz, powerMw };
}

function parseKeyValueCsv(line: string): Record<string, string> {
  const result: Record<string, string> = {};
  const [, ...segments] = line.split(',');
  segments.forEach((segment) => {
    const [rawKey, ...valueParts] = segment.split('=');
    if (!rawKey || valueParts.length === 0) {
      return;
    }
    result[rawKey.trim()] = valueParts.join('=').trim();
  });
  return result;
}

function numberOrNull(value: string | null | undefined): number | null {
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function boolFrom01(value: string | null | undefined): boolean {
  return value === '1' || value === 'true';
}

function mapRuntimePhase(value: string): AutotuneRuntimePhase | null {
  if (value === 'DISCOVER') {
    return 'discover';
  }
  if (value === 'LOCAL_SEARCH') {
    return 'local_search';
  }
  if (value === 'TRACK') {
    return 'track';
  }
  return null;
}
